import { Colors } from 'styles';
import { Stack } from 'common/ui/Layout';
import { css } from 'styled-components';
import { useState } from 'react';
import type { AllReporterVariant } from '../hooks/useReporter';
import { headingErrorState } from '../../Reporter/state';
import { useRecoilValue } from 'recoil';
import { getDuplicateHeadingErrorText } from '../plugins/headingError/utils';
import Text from 'common/ui/Text';
import useResizeAware from 'react-resize-aware';
import { createContext } from 'common/ui/util';
import type { CreateContextReturn } from 'common/ui/util';
import { useSetUnifiedEditorElement } from './UnifiedEditorElementContext';
import { useCurrentUser } from 'hooks/useCurrentUser/legacy';
import { useImpressionGenerator } from 'hooks/useImpressionGenerator';
import { useCompactUI } from 'hooks/useCompactUI';
import { UnifiedEditorTopBar } from './UnifiedEditorTopBar';
import { useReportLockedState } from '../../Reporter/hooks/useReportLockedState';
import { reporterSavingState } from 'config/recoilState';
import { useCurrentCaseReport } from 'hooks/useCurrentCaseReport';
import { useFocusMode } from 'hooks/useFocusMode';
import { useLineIndicator } from '../hooks/useLineIndicator';
import { useReportTemplate } from 'hooks/useReportTemplate';
import { telemetryContextFor } from 'modules/Apollo/types';
import { useSlateStatic, useSlateSelection } from '../core';
import { rootTheme } from 'theme/rootTheme';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { recorderState } from 'common/Recorder/useRecorder/state';
import { RECORDER_EDITOR_RED_DARK, pulseRedEditorBorder } from 'common/Recorder/styles';

export const UNIFIED_EDITOR_PADDING = 12;
export const UNIFIED_EDITOR_VERTICAL_PADDING = 8;

type UnifiedEditorSizesContextType = {
  height: number;
  width: number;
};

export const [
  UnifiedEditorSizesProvider,
  useUnifiedEditorSizes,
  UnifiedEditorSizesContext,
]: CreateContextReturn<Nullable<UnifiedEditorSizesContextType>> = createContext<
  Nullable<UnifiedEditorSizesContextType>
>({
  name: 'unified-editor-sizes',
  errorMessage: 'useUnifiedEditorSizes must be used within a UnifiedEditorSizesProvider',
  defaultValue: {
    width: 0,
    height: 0,
  },
});

const ZERO_SIZES = { width: 0, height: 0 } as const;

const recorderBorderCss = css`
  border-color: ${RECORDER_EDITOR_RED_DARK};
  box-shadow: inset 0 0 0 2px ${RECORDER_EDITOR_RED_DARK};
  animation: ${pulseRedEditorBorder} 2s infinite ease-in-out;
`;

export type UnifiedEditorProps = {
  isDisabled?: boolean;
  variant: AllReporterVariant;
  children: React.ReactNode;
  isAddendumEditor?: boolean;
  label?: Nullable<string>;
  containerCss?: Nullable<string>;
  addendumSMID?: Nullable<string>;
  addendumOwner?: { firstName: string; lastName: string };
  addendumSubmittedAt?: Nullable<Date>;
};

export const UnifiedEditor = ({
  isDisabled = false,
  variant = 'report',
  children,
  isAddendumEditor = false,
  label,
  containerCss,
  addendumSMID,
  addendumOwner,
  addendumSubmittedAt,
}: UnifiedEditorProps): React.ReactElement => {
  const [focused, setIsFocused] = useState(false);
  const setUnifiedEditorElement = useSetUnifiedEditorElement();
  const hasHeadingError = useRecoilValue<boolean>(headingErrorState);
  const [resizeListener, sizes] = useResizeAware();
  const { data } = useCurrentUser({ context: APOLLO_QUERY_CONTEXT });
  const lineIndicator = data?.me?.reporterSettings?.lineIndicator;
  const { isCurrentImpressionGenerated } = useImpressionGenerator();
  const editor = useSlateStatic();
  const selection = useSlateSelection();
  const { showCompactHeight } = useCompactUI();
  const { isClaimedByMe, isSubmittedStatus, isAddendumStatus } = useReportLockedState({
    context: APOLLO_QUERY_CONTEXT,
  });
  const { template } = useReportTemplate({ context: APOLLO_QUERY_CONTEXT });
  const isInProgressAndClaimedByMe = isClaimedByMe && !isSubmittedStatus;
  const isAddendumStatusAndEditor = isAddendumStatus && isAddendumEditor;
  const isFinalizedAddendum = isAddendumEditor && variant === 'view';
  const saveStatus = useRecoilValue(reporterSavingState);
  const [isRecorderBorderEnabled] = useFeatureFlagEnabled(FF.REPORTER_TEXT_EDITOR_RECORDER_BORDER);
  const isRecording = useRecoilValue(recorderState);

  const { currentCaseReport: currentCase } = useCurrentCaseReport();

  const { isFocusModeSettingVisible, isFocusModeSettingEnabled, isFocusModeOn, toggleFocusMode } =
    useFocusMode({ context: APOLLO_QUERY_CONTEXT });
  const isFocusModeSettingOn = isFocusModeSettingVisible && isFocusModeSettingEnabled;

  const currentReport = currentCase?.report;
  const currentAddendum = (currentReport?.addendums ?? []).find((addendum) => {
    return addendum.smid === addendumSMID;
  });

  const lastUpdated = isAddendumEditor
    ? currentAddendum?.updated
    : currentReport?.sectionsLastUpdated;

  const isReadOnly = variant === 'view' || isDisabled;
  const isEditableByMe =
    (!isAddendumStatus && isInProgressAndClaimedByMe) || isAddendumStatusAndEditor;
  const isReportEditor = variant === 'report';
  const shouldShowStatusIndicator = isReportEditor && !isReadOnly && isEditableByMe;
  const shouldShowFocusModeToggle =
    isReportEditor && !isReadOnly && isEditableByMe && !isAddendumStatusAndEditor;
  const shouldShowTemplateName = isReportEditor && isEditableByMe && !isAddendumEditor;
  const shouldShowRecorderTextEditorBorder = isRecorderBorderEnabled && isRecording && !isReadOnly;

  const reportOwner = currentReport?.owner;
  const reportSubmittedAt = currentReport?.submittedAt;
  const { updatePosition: updateLineIndicatorPosition } = useLineIndicator();

  return (
    <Stack
      ref={setUnifiedEditorElement}
      id="unified-editor-wrapper"
      vertical
      stretchX
      stretchY={!isFinalizedAddendum}
      data-testid="unified-editor-wrapper"
      css={css`
        ${!isFinalizedAddendum ? 'flex: 1;' : ''}
        max-width: 100%;
        ${containerCss}
      `}
    >
      <UnifiedEditorTopBar
        isFocusModeSettingOn={isFocusModeSettingOn}
        toggleFocusMode={toggleFocusMode}
        isFocusModeOn={isFocusModeOn}
        label={label}
        isReportEditor={isReportEditor}
        saveStatus={saveStatus}
        lastUpdated={lastUpdated}
        reportOwner={reportOwner}
        reportSubmittedAt={reportSubmittedAt}
        isAddendumEditor={isAddendumEditor}
        addendumOwner={addendumOwner}
        addendumSubmittedAt={addendumSubmittedAt}
        shouldShowFocusModeToggle={shouldShowFocusModeToggle}
        shouldShowStatusIndicator={shouldShowStatusIndicator}
        shouldShowTemplateName={shouldShowTemplateName}
        templateName={template?.name ?? undefined}
      />
      <Stack
        data-stack-stretch
        vertical
        data-testid="unified-editor-text-area"
        onScroll={() => {
          if (selection == null) {
            return;
          }
          updateLineIndicatorPosition({ editor, selection });
        }}
        onFocus={setIsFocused}
        onBlur={() => setIsFocused(false)}
        css={css`
          box-sizing: border-box;
          width: 100%;
          margin-top: 2px;
          padding-top: ${UNIFIED_EDITOR_VERTICAL_PADDING}px;
          padding-bottom: ${UNIFIED_EDITOR_VERTICAL_PADDING}px;
          padding-left: ${lineIndicator?.variant === 'dot'
            ? lineIndicator.placement === 'left'
              ? UNIFIED_EDITOR_PADDING + 8
              : UNIFIED_EDITOR_PADDING
            : UNIFIED_EDITOR_PADDING}px;
          padding-right: ${lineIndicator?.variant === 'dot'
            ? lineIndicator.placement === 'right'
              ? UNIFIED_EDITOR_PADDING + 8
              : UNIFIED_EDITOR_PADDING
            : UNIFIED_EDITOR_PADDING}px;
          height: ${isFinalizedAddendum ? 'auto' : isAddendumEditor ? '20vh' : '100%'};
          background-color: ${isDisabled ? Colors.gray5 : Colors.gray2};
          border: 1px solid
            ${hasHeadingError && variant === 'report'
              ? Colors.red6
              : focused
                ? Colors.mainBlue
                : Colors.gray8};
          border-radius: ${rootTheme.radius.sm}px;
          ${shouldShowRecorderTextEditorBorder ? recorderBorderCss : ''}
          overflow: auto;
          overflow-wrap: anywhere;
          position: relative;
          ${isAddendumEditor && !isFinalizedAddendum ? 'max-height: 20vh; min-height: 20vh;' : ''}
          &::-webkit-scrollbar {
            display: none;
          }
          scrollbar-width: none;
        `}
      >
        {resizeListener}
        <UnifiedEditorSizesProvider value={sizes ?? ZERO_SIZES}>
          {children}
        </UnifiedEditorSizesProvider>
      </Stack>
      {!isFinalizedAddendum && (
        <Stack
          data-testid="unified-editor-bottom-info-bar"
          alignX="between"
          space="small"
          css={{
            marginLeft: 16,
            ...(variant !== 'report' || showCompactHeight
              ? {}
              : { marginTop: 4, height: 16, marginBottom: 4 }),
          }}
        >
          <Text variant="caption" color={Colors.gray8}>
            {editor != null && isCurrentImpressionGenerated(editor)
              ? 'Impression has been generated by artificial intelligence.'
              : ''}
          </Text>
          {hasHeadingError && variant === 'report' && (
            <Text variant="caption" color="error">
              {getDuplicateHeadingErrorText(variant)}
            </Text>
          )}
        </Stack>
      )}
    </Stack>
  );
};

UnifiedEditor.displayName = 'UnifiedEditor';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(UnifiedEditor);
